import Delta from "quill-delta";
import type {RichTextDocument, RichTextDocumentNode} from "@o2d/rich-text-editor";
import type {RteValue} from "../domain/rte-value.type";
import {QuillDeltaToRichTextDocumentConverter} from "./quill-delta-to-rich-text-document.helper";
import type {DeltaConversionReport} from "./quill-delta-to-rich-text-document.helper";

/**
 * Shape-dispatch for `RteValue` (`Delta | RichTextDocument`) — the one place
 * that tells the two apart and normalizes between them, so every consumer of
 * a migrating rich-text field (`ha-rte`, `ha-quill-viewer`, the entity/mapper
 * layer) branches through the SAME check. See `RteValue`'s own doc comment
 * for the migration this exists to bridge.
 */
export class RteValueHelper {

    public static isRichTextDocument(value: unknown): value is RichTextDocument {
        return typeof value === "object" && value !== null && !Array.isArray(value)
            && (value as { type?: unknown }).type === "doc";
    }

    public static isQuillDelta(value: unknown): value is { ops: unknown[] } {
        return typeof value === "object" && value !== null
            && Array.isArray((value as { ops?: unknown }).ops);
    }

    /** A fresh, empty document — what a brand-new field (a just-added
     *  LinkTile, a new Issue) starts as, instead of an empty Quill `Delta`
     *  that would just get converted right back on first render. */
    public static empty(): RichTextDocument {
        return {type: "doc", content: [{type: "paragraph"}]};
    }

    /**
     * A deep copy that keeps the shape: a Delta stays a Delta, a document stays a document, null
     * stays null. Nothing is converted.
     *
     * Used where a value must not share objects with its source: `Issue.clone()`, so editing the
     * clone cannot change the original, and `ha-rte`, which hands its editor a fresh object on every
     * value change, because the editor reloads only on a new reference (switching back to an issue
     * otherwise showed the text of the issue before).
     *
     * The Delta branch copies `ops` itself: `new Delta(ops)` keeps the array it is given, so a copy
     * built from the original array would still share it (found in Review-Lauf 02 of the RTE
     * unification, `issue.entity.test.ts`).
     */
    public static copy(value: RichTextDocument): RichTextDocument;
    public static copy(value: RteValue): RteValue;
    public static copy(value: RteValue | null): RteValue | null;
    public static copy(value: RteValue | null): RteValue | null {
        if (RteValueHelper.isQuillDelta(value)) {
            return new Delta(JSON.parse(JSON.stringify(value.ops)));
        }
        return JSON.parse(JSON.stringify(value));
    }

    /** Normalizes any `RteValue` into a `RichTextDocument`, converting a
     *  legacy Delta lazily (see `QuillDeltaToRichTextDocumentConverter`).
     *  Idempotent: an already-`RichTextDocument` value passes through
     *  untouched. */
    public static toRichTextDocument(value: RteValue | null | undefined): RichTextDocument {
        if (!value) {
            return RteValueHelper.empty();
        }
        if (RteValueHelper.isRichTextDocument(value)) {
            return value;
        }
        return QuillDeltaToRichTextDocumentConverter.convert(value);
    }

    /**
     * Like `toRichTextDocument`, but hands back the converter's report instead of dropping it.
     *
     * The plain method above is used in hot paths — `issue-form.service.ts` calls it twice per
     * dirty-check, i.e. on every keystroke — so it must stay silent. This one is for the single
     * place where a conversion is about to become a STORED value: `ha-rte`, when the user starts
     * editing a description that is still a legacy Delta. The deliberate losses of that conversion
     * (a bullet indented under no parent, a colour on an image, a broken `//:0` address) happen
     * either way; without this they happen uncounted, spread over weeks, instead of once and
     * measurable — which is what the one-time migration would have given us.
     *
     * `report` is null when nothing was converted: the value was already a document, or empty.
     * That distinction is the point — see `ha-rte`'s logging, which must be able to tell
     * "converted, lost nothing" from "no conversion happened here at all".
     */
    public static toRichTextDocumentWithReport(
        value: RteValue | null | undefined,
    ): {document: RichTextDocument; report: DeltaConversionReport | null} {
        if (!value) {
            return {document: RteValueHelper.empty(), report: null};
        }
        if (RteValueHelper.isRichTextDocument(value)) {
            return {document: value, report: null};
        }
        const report = QuillDeltaToRichTextDocumentConverter.convertWithReport(value);
        return {document: report.document, report};
    }

    /** `true` iff the value carries no user-visible content — an unset
     *  field, an empty Delta (Quill's own "nothing typed" shape,
     *  `ops: [{insert: '\n'}]`), or a `RichTextDocument` whose only content
     *  is an empty paragraph. */
    public static hasContent(value: RteValue | null | undefined): boolean {
        if (!value) {
            return false;
        }
        if (RteValueHelper.isRichTextDocument(value)) {
            return (value.content ?? []).some(node => RteValueHelper._nodeHasContent(node));
        }
        if (RteValueHelper.isQuillDelta(value)) {
            const ops = value.ops as { insert?: unknown }[];
            return ops.some(op => typeof op.insert === "string" ? op.insert.trim().length > 0 : !!op.insert);
        }
        return false;
    }

    private static _nodeHasContent(node: RichTextDocumentNode): boolean {
        if (node.type === "text") {
            return (node.text ?? "").trim().length > 0;
        }
        if (node.type === "image" || node.type === "hardBreak") {
            return true;
        }
        return (node.content ?? []).some(child => RteValueHelper._nodeHasContent(child));
    }

    /** Plain text, formatting stripped — for a non-rendered context (a log
     *  line, a search index, a confirm dialog) where mounting a live editor
     *  purely to read text back out would be wasteful. */
    public static plainText(value: RteValue | null | undefined): string {
        if (!value) {
            return "";
        }
        if (RteValueHelper.isRichTextDocument(value)) {
            return (value.content ?? []).map(node => RteValueHelper._nodePlainText(node)).join("\n").trim();
        }
        if (RteValueHelper.isQuillDelta(value)) {
            return (value.ops as { insert?: unknown }[])
                .map(op => typeof op.insert === "string" ? op.insert : "")
                .join("")
                .trim();
        }
        return "";
    }

    private static _nodePlainText(node: RichTextDocumentNode): string {
        if (node.type === "text") {
            return node.text ?? "";
        }
        return (node.content ?? []).map(child => RteValueHelper._nodePlainText(child)).join("");
    }

}
